import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BaseInstance from '../../../../Services/AxiosInstance/BaseInstance';
import { MODELS } from '../../../../Services/Endpoint';

interface AgentConfigPanelProps {
  nodeId: string;
  nodeData: {
    label: string;
    provider?: string;
    model?: string;
    apiKey?: string;
    instructions?: string;
  };
  onSave: (nodeId: string, data: any) => void;
  onClose: () => void;
}

// API response format matches the visual builder
interface ProvidersResponse {
  [providerName: string]: string[]; // e.g., { "OpenAI": ["gpt-4o", "gpt-4o-mini"], "Anthropic": ["claude-3-opus"] }
}

const AgentConfigPanel: React.FC<AgentConfigPanelProps> = ({
  nodeId,
  nodeData,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(nodeData.label || '');
  const [provider, setProvider] = useState(nodeData.provider || '');
  const [model, setModel] = useState(nodeData.model || '');
  const [apiKey, setApiKey] = useState(nodeData.apiKey || '');
  const [instructions, setInstructions] = useState(nodeData.instructions || '');
  const [providers, setProviders] = useState<ProvidersResponse>({});
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch providers from API
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(MODELS);
        setProviders(response.data);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        // Fallback to empty object if API fails
        setProviders({});
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  // Sync when a different Agent node is selected while panel is open
  useEffect(() => {
    setName(nodeData.label || '');
    setProvider(nodeData.provider || '');
    setModel(nodeData.model || '');
    setApiKey(nodeData.apiKey || '');
    setInstructions(nodeData.instructions || '');
  }, [nodeId, nodeData.label, nodeData.provider, nodeData.model, nodeData.apiKey, nodeData.instructions]);

  // Update models when provider changes
  useEffect(() => {
    if (!provider) {
      setModels([]);
      return;
    }

    // Get models for the selected provider from the API data
    const providerModels = providers[provider] || [];
    setModels(providerModels);
  }, [provider, providers]);

  const handleSave = () => {
    const updatedData = {
      label: name,
      provider,
      model,
      apiKey,
      instructions,
    };
    onSave(nodeId, updatedData);
  };

  return (
    <Paper
      sx={{
        width: 400,
        height: '100vh',
        p: 3,
        borderRadius: 0,
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        right: 0,
        zIndex: 1200,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Configure Agent
        </Typography>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Configuration Form */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto', pt: 1 }}>
        {/* Node Name */}
        <TextField
          label="Node Name"
          value={name}
          onChange={(e) => {
            if (e.target.value.length <= 30) {
              setName(e.target.value)
            }
          }}
          fullWidth
          variant="outlined"
          size="small"
        />

        {/* Provider Selection */}
        <FormControl fullWidth size="small">
          <InputLabel>Provider</InputLabel>
          <Select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              setModel(''); // Reset model when provider changes
            }}
            label="Provider"
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading providers...
              </MenuItem>
            ) : (
              Object.keys(providers).map((providerName) => (
                <MenuItem key={providerName} value={providerName}>
                  {providerName}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Model Selection */}
        <FormControl fullWidth size="small">
          <InputLabel>Model</InputLabel>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            label="Model"
            disabled={loading || !provider}
          >
            {models.map((modelName) => (
              <MenuItem key={modelName} value={modelName}>
                {modelName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* API Key */}
        <TextField
          label="API Key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          type="password"
          placeholder="Enter your API key for the selected provider"
          helperText="Your API key will be securely stored and used for this agent"
        />

        {/* Instructions */}
        <TextField
          label="Instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          fullWidth
          multiline
          rows={6}
          variant="outlined"
          size="small"
          placeholder="Enter instructions for the agent..."
          required
        />
      </Box>

      {/* Save Button */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          fullWidth
          disabled={!name || !provider || !model || !apiKey || !instructions.trim()}
        >
          Save Configuration
        </Button>
      </Box>
    </Paper>
  );
};

export default AgentConfigPanel;
